import React, { useState, useEffect } from 'react';
import { Trash2, Info } from 'lucide-react';
import ReferenceSelect from './ReferenceSelect';
import { retrieveViews } from '../services/database';

const ProvideReference = ({ projectId, onReferenceChange = () => {} }) => {
  const [showModal, setShowModal] = useState(false);
  const [hasViews, setHasViews] = useState(false);
  const [reference, setReference] = useState(null);
  const [views, setViews] = useState([]);

  // Fetch views to determine if the project has any existing views
  useEffect(() => {
    const fetchViews = async () => {
      try {
        if (projectId) {
          const fetchedViews = await retrieveViews(projectId);
          
          // Filter to include only views with completed versions
          const validViews = fetchedViews.filter(view => {
            const completedVersions = (view.versions || []).filter(
              version => !version.revisionStatus || version.revisionStatus === 'complete'
            );
            return completedVersions.length > 0;
          });
          
          setViews(validViews);
          setHasViews(validViews.length > 0);
        }
      } catch (error) {
        console.error('Error fetching views for reference:', error);
        setHasViews(false);
      }
    };

    fetchViews();
  }, [projectId]);

  const handleOpenModal = (e) => {
    // Prevent form submission when clicking the button
    e.preventDefault();
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleSaveReference = (selectedReference) => {
    setReference(selectedReference);
    onReferenceChange(selectedReference);
    setShowModal(false);
  };

  const handleRemoveReference = (e) => {
    // Prevent form submission when clicking the button
    e.preventDefault();
    setReference(null);
    onReferenceChange(null);
  };

  // If there are no views, don't render anything
  if (!hasViews) {
    return null;
  }

  return (
    <div className="my-4">
      {!reference ? (
        <div className="flex items-center">
          <button
            type="button" // Important: specify type="button" to prevent form submission
            onClick={handleOpenModal}
            className="text-black font-medium hover:underline flex items-center"
          >
            Provide a reference view
          </button>
          <div className="relative group ml-2">
            <Info size={16} className="text-gray-500 cursor-help" />
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-64 pointer-events-none">
              To create the exact same components or to borrow designs from a view that you created earlier
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center">
          <button
            type="button" // Important: specify type="button" to prevent form submission
            onClick={handleOpenModal}
            className="text-black font-medium hover:underline mr-2"
          >
            {reference.viewTitle} (version {reference.versionIndex})
          </button>
          <button
            type="button" // Important: specify type="button" to prevent form submission
            onClick={handleRemoveReference}
            className="text-red-500 hover:text-red-700"
            title="Remove reference"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )}

      {showModal && (
        <ReferenceSelect
          isOpen={showModal}
          onClose={handleCloseModal}
          onSave={handleSaveReference}
          views={views}
          initialReference={reference}
          projectId={projectId}
        />
      )}
    </div>
  );
};

export default ProvideReference;